import { useState, useCallback, useRef, DragEvent } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { validateImageFile, getCroppedImage, getBase64Size } from '@/lib/imageUtils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';

interface ProfilePictureUploadProps {
  open: boolean;
  onClose: () => void;
  currentAvatarUrl?: string | null;
  onUploadSuccess: (avatarUrl: string | null) => void;
}

export function ProfilePictureUpload({
  open,
  onClose,
  currentAvatarUrl,
  onUploadSuccess,
}: ProfilePictureUploadProps) {
  const [, setSelectedFile] = useState<File | null>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Process a file (shared between file input and drag-drop)
  const processFile = useCallback((file: File) => {
    // Validate file
    const validation = validateImageFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    // Load image for preview
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result as string);
      setSelectedFile(file);
      // Reset crop settings
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.onerror = () => {
      toast.error('Failed to load image');
    };
    reader.readAsDataURL(file);
  }, []);

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    processFile(file);
  };

  // Drag and drop handlers
  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0 && files[0]) {
      processFile(files[0]);
    }
  };

  // Handle crop complete
  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Handle upload
  const handleUpload = async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    setUploading(true);
    try {
      // Crop image client-side
      const croppedBase64 = await getCroppedImage(imageSrc, croppedAreaPixels, 400);

      // Validate size (after cropping, should be well under 5MB)
      // Backend accepts up to 5MB but cropped 400x400 images are typically much smaller
      const sizeInBytes = getBase64Size(croppedBase64);
      if (sizeInBytes > 5 * 1024 * 1024) {
        toast.error('Image is too large. Please choose a smaller image.');
        setUploading(false);
        return;
      }

      // Upload to backend
      const response = await api.post<{ profile: { avatarUrl: string } }>('/users/me/avatar', {
        avatarBase64: croppedBase64,
      });

      toast.success('Profile picture updated!');
      onUploadSuccess(response.profile.avatarUrl);

      // Reset state
      setImageSrc(null);
      setSelectedFile(null);
      setCroppedAreaPixels(null);
      onClose();
    } catch (error: any) {
      console.error('Upload failed:', error);

      const errorMessage = error.response?.data?.error?.message || 'Failed to upload profile picture';
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  // Handle remove avatar
  const handleRemove = async () => {
    try {
      await api.post('/users/me/avatar', { avatarBase64: null });
      toast.success('Profile picture removed');
      onUploadSuccess(null);
      onClose();
    } catch (error) {
      toast.error('Failed to remove profile picture');
    }
  };

  // Handle change image
  const handleChangeImage = () => {
    setImageSrc(null);
    setSelectedFile(null);
    setCroppedAreaPixels(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  // Handle close
  const handleClose = () => {
    setImageSrc(null);
    setSelectedFile(null);
    setCroppedAreaPixels(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Upload Profile Picture</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {!imageSrc ? (
            // File selection UI with drag and drop
            <div
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragging
                  ? 'border-primary bg-primary/10'
                  : 'border-border hover:border-primary/50'
              }`}
            >
              <Input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                onChange={handleFileSelect}
                className="hidden"
                id="avatar-upload"
              />
              <Label htmlFor="avatar-upload" className="cursor-pointer">
                <div className="space-y-2">
                  <Upload className="mx-auto h-12 w-12 text-muted-foreground" />
                  <p className="text-sm font-medium">
                    {isDragging ? 'Drop image here' : 'Click to upload or drag and drop'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    JPEG or PNG, max 5MB
                  </p>
                </div>
              </Label>
            </div>
          ) : (
            // Cropping UI
            <div className="space-y-4">
              <div className="relative h-64 bg-black rounded-lg overflow-hidden">
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </div>

              {/* Zoom slider */}
              <div className="space-y-2">
                <Label className="text-sm">Zoom</Label>
                <Slider
                  value={[zoom]}
                  min={1}
                  max={3}
                  step={0.1}
                  onValueChange={(val) => {
                    const newZoom = val[0];
                    if (typeof newZoom === 'number') {
                      setZoom(newZoom);
                    }
                  }}
                  className="w-full"
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          {imageSrc ? (
            <>
              <Button variant="outline" onClick={handleChangeImage} disabled={uploading}>
                Change Image
              </Button>
              <Button onClick={handleUpload} disabled={uploading}>
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  'Upload'
                )}
              </Button>
            </>
          ) : (
            <>
              {currentAvatarUrl && (
                <Button variant="destructive" onClick={handleRemove}>
                  Remove Current
                </Button>
              )}
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
